<script lang="ts" setup>
const props = defineProps<{
    open?: boolean;
}>();
const route = useRoute();
const activeTab = ref<string>((route.params.tab as string) || "profile");
const backPath = ref<string>("/");

const emit = defineEmits<{
    (e: "update:open", v: boolean);
}>();

const isOpen = ref<boolean>(props.open);

watch(isOpen, (to: boolean) => {
    if (props.open === to) {
        const href = window.location.href;
        backPath.value = href.replace(window.location.origin, "");
        window.history.pushState({}, "", `/settings/${activeTab.value}`);
    }
    if (to !== props.open) {
        emit("update:open", to);
        window.history.pushState({}, "", backPath.value);
    }
});

watch(activeTab, (to: string) => {
    window.history.pushState({}, "", `/settings/${to}`);
});

watch(
    () => props.open,
    (to: boolean) => {
        if (to !== isOpen.value) isOpen.value = to;
    },
);

function toggle() {
    isOpen.value = !isOpen.value;
}

defineShortcuts({
    ",": {
        usingInput: false,
        handler: () => toggle(),
    },
});
</script>

<template>
    <div>
        <slot :is-open="isOpen" :toggle="toggle" />

        <UModal
            v-model="isOpen"
            :ui="{
                width: 'w-screen xl:max-w-4xl sm:max-w-3xl',
                height: 'h-[calc(100vh-12rem)]',
                background: 'bg-white',
                padding: '',
            }"
        >
            <div class="flex items-start divide-x divide-gray-300 h-full">
                <div class="w-64 p-4 h-full bg-hd-neutral-50 flex flex-col">
                    <div class="flex items-center justify-between mb-4">
                        <h1 class="text-lg font-medium">Settings</h1>
                    </div>
                    <div class="flex flex-col gap-4">
                        <div>
                            <div class="py-2">
                                <p
                                    class="text-sm font-medium font-display text-gray-500"
                                >
                                    User
                                </p>
                            </div>
                            <ul class="w-full rounded-box p-0">
                                <li
                                    :class="[
                                        'cursor-pointer py-1',
                                        activeTab === 'profile'
                                            ? 'font-medium px-3 mb-1 bg-gray-300 rounded-md'
                                            : '',
                                    ]"
                                    @click="activeTab = 'profile'"
                                >
                                    <FontAwesomeIcon
                                        :icon="faUser"
                                        class="mr-2"
                                    />
                                    Profile
                                </li>
                            </ul>
                        </div>
                        <div>
                            <div class="py-2">
                                <p
                                    class="text-sm font-medium font-display text-gray-500"
                                >
                                    Account
                                </p>
                            </div>
                            <ul class="w-full rounded-box p-0">
                                <li
                                    :class="[
                                        'cursor-pointer py-1',
                                        activeTab === 'people'
                                            ? 'font-medium px-3 mb-1 bg-gray-300 rounded-md'
                                            : '',
                                    ]"
                                    @click="activeTab = 'people'"
                                >
                                    <FontAwesomeIcon
                                        :icon="faUserGroupSimple"
                                        class="mr-2"
                                    />
                                    People
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <!-- Preferences Tab -->
                <AppSettingPreferencesTab v-if="activeTab === 'preferences'" />
                <!-- Settings Tab -->
                <AppSettingProfileTab v-if="activeTab === 'profile'" />
                <!-- People Tab -->
                <AppSettingPeopleTab v-if="activeTab === 'people'" />
            </div>
        </UModal>
    </div>
</template>
